import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, MessagesSquare, CheckCircle2, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const SurveysPage = () => {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(null);

  const load = () => api.get("/surveys").then(({ data }) => setSurveys(data));
  useEffect(() => { load(); }, []);

  const setAnswer = (sid, qid, val) => {
    setAnswers((a) => ({ ...a, [sid]: { ...(a[sid] || {}), [qid]: val } }));
  };

  const submit = async (s) => {
    if (!user) { toast.error("Connecte-toi pour répondre"); return; }
    const ans = answers[s.survey_id] || {};
    if (Object.keys(ans).length < (s.questions?.length || 0)) {
      toast.error("Réponds à toutes les questions");
      return;
    }
    setSubmitting(s.survey_id);
    try {
      const { data } = await api.post(`/surveys/${s.survey_id}/respond`, { answers: ans });
      toast.success(`Merci ! +${data.reward_vola} Vola crédités 💰`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur");
    } finally { setSubmitting(null); }
  };

  return (
    <div className="px-4 md:px-12 py-8 max-w-4xl mx-auto pb-32" data-testid="surveys-page">
      <div className="mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-yellow/15 text-brand-yellow text-xs font-bold uppercase tracking-widest mb-3">
          <Sparkles size={11} /> Donne ton avis
        </span>
        <h1 className="font-display text-3xl md:text-5xl font-black mb-2">Sondages Voamada</h1>
        <p className="text-white/60">Réponds à des sondages courts et gagne des <strong className="text-brand-yellow">Vola</strong> que tu pourras échanger contre du Mvola, du merch ou Premium.</p>
      </div>

      {!user && (
        <div className="rounded-2xl border border-brand-yellow/30 bg-brand-yellow/5 p-5 mb-6 text-center">
          <p className="text-sm text-white/80 mb-2">Connecte-toi pour répondre et gagner des Vola.</p>
          <Link to="/login" className="btn-primary inline-block">Se connecter</Link>
        </div>
      )}

      {surveys.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <MessagesSquare size={40} className="mx-auto text-white/30 mb-3" />
          <p className="text-white/50">Aucun sondage actif pour l'instant. Reviens bientôt !</p>
        </div>
      )}

      <div className="space-y-5">
        {surveys.map((s) => {
          const answered = !!s.answered;
          return (
            <div key={s.survey_id} className={`rounded-2xl border p-5 md:p-6 transition ${
              answered ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"
            }`} data-testid={`survey-${s.survey_id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center">
                  <MessagesSquare size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-lg md:text-xl font-black mb-1">{s.title}</h2>
                  {s.description && <p className="text-sm text-white/60">{s.description}</p>}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-yellow">
                      <Coins size={11} /> +{s.reward_vola} Vola
                    </span>
                    <span className="text-xs text-white/40">· {s.responses_count} réponses</span>
                    {s.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 uppercase">{s.category}</span>}
                  </div>
                </div>
                {answered && <CheckCircle2 size={20} className="text-emerald-400 shrink-0" />}
              </div>

              {!answered && user && (
                <>
                  <div className="space-y-3 mb-4">
                    {s.questions?.map((q, qi) => (
                      <div key={q.question_id} className="rounded-xl bg-black/30 border border-white/5 p-4">
                        <p className="text-sm font-bold mb-2"><span className="text-brand-orange">{qi + 1}.</span> {q.question_text}</p>
                        {q.question_type === "choice" && Array.isArray(q.options) && (
                          <div className="space-y-1.5">
                            {q.options.map((opt, oi) => (
                              <label key={oi} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-2 py-1.5 rounded-lg">
                                <input type="radio" name={q.question_id}
                                  checked={(answers[s.survey_id]?.[q.question_id]) === opt}
                                  onChange={() => setAnswer(s.survey_id, q.question_id, opt)}
                                  data-testid={`q-${q.question_id}-opt-${oi}`} />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {q.question_type === "text" && (
                          <textarea rows={2}
                            value={answers[s.survey_id]?.[q.question_id] || ""}
                            onChange={(e) => setAnswer(s.survey_id, q.question_id, e.target.value)}
                            className="w-full mt-1 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                            data-testid={`q-${q.question_id}-text`} />
                        )}
                        {q.question_type === "rating" && (
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((r) => (
                              <button key={r} type="button"
                                onClick={() => setAnswer(s.survey_id, q.question_id, r)}
                                className={`w-10 h-10 rounded-lg border text-sm font-bold transition ${
                                  answers[s.survey_id]?.[q.question_id] === r
                                    ? "bg-brand-yellow text-brand-dark border-brand-yellow"
                                    : "bg-white/5 text-white/60 border-white/10 hover:border-white/30"
                                }`}
                                data-testid={`q-${q.question_id}-rating-${r}`}>{r}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => submit(s)} disabled={submitting === s.survey_id}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid={`submit-survey-${s.survey_id}`}>
                    {submitting === s.survey_id ? "Envoi…" : <>Envoyer mes réponses <Send size={14} /></>}
                  </button>
                </>
              )}

              {answered && <p className="text-sm text-emerald-300">✓ Tu as déjà répondu à ce sondage. Tes Vola ont été crédités.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SurveysPage;
